/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ProactiveChatNames } from '../../helpers/constants';
import { IWindow } from '../../interfaces';
import { IChatContext, IWaitOnTimeContext, TContextProvider } from '../../../interfaces';
import { IMsdyn365CsChatConnectorViewProps } from '../../msdyn365-cs-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * WaitOnTimeTrigger class.
 * Proactive chat is triggered if user stays on current page for more than (config.proactiveOnWaitTimeSeconds) seconds.
 */
export default class WaitOnTimeTrigger extends ProactiveChatTrigger {
    private waitTimeInMilliseconds: number | undefined;

    /**
     * Class constructor.
     * @param {IMsdyn365CsChatConnectorViewProps} props The chat connector view properties.
     * @param {IChatContext} context The context.
     */
    constructor(props: IMsdyn365CsChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        if (this.props.config.proactiveOnWaitTimeSeconds && !isNaN(this.props.config.proactiveOnWaitTimeSeconds)) {
            this.waitTimeInMilliseconds = Number(this.props.config.proactiveOnWaitTimeSeconds) * 1000;
            this.props.context.telemetry.debug(
                `[Chat][${WaitOnTimeTrigger.name}] Wait time (ms) set to: '${this.waitTimeInMilliseconds}'.`
            );
        }
    }

    /**
     * Validates whether the condition(s) is met to trigger proactive chat: user stays on page longer than the specific time.
     * @returns {IContextProvider} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        const retVal: boolean = this.waitTimeInMilliseconds !== undefined;
        this.props.context.telemetry.debug(`[Chat][${WaitOnTimeTrigger.name}] Validation returning '${retVal}'.`);
        return retVal;
    }

    /**
     * Starts the chat by configuring the LiveChatWidget SDK code.
     * @returns {Promise<boolean>} True when the chat has started. Otherwise, false.
     */
    public async startChat(): Promise<boolean> {
        if (await this.validate()) {
            (<IWindow>(<unknown>window)).contextProvider = this.getContextProvider();

            this.props.context.telemetry.debug(
                `[${WaitOnTimeTrigger.name}] Chat widget "lcw:ready" event listener has been triggered within proactive chat.`
            );
            (<IWindow>(<unknown>window)).Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(this.getContextProvider());
            const message = this.getMessage();

            this.props.context.telemetry.debug(
                `[${WaitOnTimeTrigger.name}] Starting wait on-time proactive chat with '${this.waitTimeInMilliseconds}' (ms) delay and message: '${message}'.`
            );
            setTimeout(() => {
                (<IWindow>(<unknown>window)).Microsoft.Omnichannel.LiveChatWidget.SDK.startProactiveChat({ message: message }, false);
            }, this.waitTimeInMilliseconds);

            return true;
        } else {
            return false;
        }
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): IWaitOnTimeContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.WaitOnTime,
                    isDisplayable: true
                },
                TimeOnPage: {
                    value: this.waitTimeInMilliseconds ? this.waitTimeInMilliseconds : 0,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnWaitTimeMessage || this.props.config.proactiveDefaultMessage;
    }
}
